import {AgGridReact} from "ag-grid-react";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ColDef, GetRowIdParams, GridOptions, IsRowSelectable, RowNode} from "ag-grid-community";
import {Button, Stack, useTheme} from "@mui/material";
import {DeleteRounded, DownloadRounded, InfoRounded, PreviewRounded} from "@mui/icons-material";
import {ID_ERROR, ID_PROCESSING} from "../constants/IntermediariesStatuses";
import IconButton from "@mui/material/IconButton";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import {useIntermediariesPrintoutRepository} from "../../../api/repositories/IntermediariesPrintoutRepository";
import {FilterDef} from "../../../constants/FinanceModels";
import {GridApi, IServerSideGetRowsParams} from "ag-grid-enterprise";
import { useRecoilValue, useSetRecoilState} from "recoil";
import {
    STATE_PREVIEW_ACTIVE_FILTERS,
    STATE_PRINTOUT_TOTAL_COUNT
} from "../constants/IntermediariesRecoil";
import {useParams} from "react-router-dom";
import {downloadPDF, formatAmount} from "./ReportTableUtil";
import {
    SCOPE_FINANCE_DOWNLOAD_INTERMEDIARIES_PRINTOUTS,
    SCOPE_FINANCE_INTERMEDIARIES_DELETE_PRINTOUTS,
    SCOPE_FINANCE_INTERMEDIARIES_PUBLISH_PRINTOUTS
} from "../constants/ConstantsAuth";
import {useHasScope} from "../../../../../config/globalConfig";
import {formatDate} from "../../../utils/FormatUtils";
import FinanceDialog from "../../../component/FinanceDialog";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {useNavigate} from "react-router";
import {decode} from "../../../utils/Base64Utils";

const ReportPreviewTable = (props: any) => {
    const { t } = useTranslation('finance', { keyPrefix: 'intermediaries' });
    const { getPrintoutsOfReport, downloadPrintoutHTML, downloadPrintoutPdf, deleteIntermediaryPrintoutItem} = useIntermediariesPrintoutRepository();
    const theme = useTheme();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const gridRef = useRef<AgGridReact>(null);
    const filterDefs = useRecoilValue(STATE_PREVIEW_ACTIVE_FILTERS);
    const setTotalDataCount = useSetRecoilState(STATE_PRINTOUT_TOTAL_COUNT);
    const [isVisibleMail, setEmailVisible] = useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [reportToDelete, setReportToDelete] = useState({ id: '', name: '' })
    const { id } = useParams();
    const navigate = useNavigate();
    const hasScope = useHasScope;
    const setUpdating = props.setUpdating;

    const columnDefs: ColDef[] = [
        {
            field: 'validation', headerName: '',
            suppressSizeToFit: true, maxWidth: 65,
            sortable: false,
            checkboxSelection: true, showDisabledCheckboxes: true, headerCheckboxSelection: true,
            lockVisible: true, lockPinned: false
        },
        {
            field: 'intermediaryId',
            headerName: t('preview.tables.intermediaryCode'),
            headerTooltip: t('preview.tables.intermediaryCode'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'intermediaryName',
            headerName: t('preview.tables.intermediary'),
            headerTooltip: t('preview.tables.intermediary'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'totalReservation',
            headerName: t('preview.tables.totalReservation'),
            headerTooltip: t('preview.tables.totalReservation'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'totalCommissions',
            headerName: t('preview.tables.commission'),
            headerTooltip: t('preview.tables.commission'),
            cellRenderer: (params: any) => formatAmount(params.value, 0.01),
            tooltipValueGetter: (params: any) => formatAmount(params.value, 0.01),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'commissionRate',
            headerName: t('preview.tables.percentage'),
            headerTooltip: t('preview.tables.percentage'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'taxWithholder',
            headerName: t('preview.tables.tk'),
            headerTooltip: t('preview.tables.tk'),
            minWidth: 150,
            unSortIcon: true,
        },
        {
            field: 'intermediaryEmail', headerName: t('preview.tables.intermediaryEmail'),
            headerTooltip: t('preview.tables.intermediaryEmail'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => params.value ? params.value : "---",
            tooltipValueGetter: (params: any) => params.value
        },
        {
            field: 'publishEmailDate', headerName: t('preview.tables.publishEmailDate'), headerTooltip: t('preview.tables.publishEmailDate'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'errorReason',
            headerName: t('tables.printout.errorReason'),
            headerTooltip: t('tables.printout.errorReason'),
            minWidth: 200,
            unSortIcon: true,
            cellRenderer: (params:any) => params?.value ? t("printout-wizard.snackbars.handleErrors." + params.value) : "",
            tooltipValueGetter: (params:any) => params?.value ? t("printout-wizard.snackbars.handleErrors." + params.value) : ""
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            hide: !hasScope(SCOPE_FINANCE_DOWNLOAD_INTERMEDIARIES_PRINTOUTS),
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                        <FinanceDynamicActions>
                            <IconButton title={t('preview.buttons.preview')} sx={{ color: theme.palette.accent.main }}
                                disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR } onClick={() => {
                                    downloadPrintoutHTML(params.data.reportId, params.data.intermediaryId).then((res) => {
                                        if (res?.result) {
                                            let pdfWindow = window.open("");
                                            pdfWindow!.document.write(decode(res.result.file));
                                        } else {
                                            dispatchGlobalData({
                                                type: setSnackbar,
                                                payload: {
                                                    snackbar: {
                                                        message: res.message !== "" ? t("printout-wizard.snackbars.handleErrors." + res.message): t("printout-wizard.snackbars.handleErrors.errors.generic"),
                                                        type: snackbarTypes.error,
                                                        show: true,
                                                        autoClose: true
                                                    }
                                                }
                                            });
                                        }
                                    });
                                }}>
                                <PreviewRounded />
                            </IconButton>
                            <IconButton title={t('preview.buttons.download')} sx={{ color: theme.palette.accent.main }}
                                disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR} onClick={() => {
                                    downloadPrintoutPdf(params.data.reportId, params.data.intermediaryId).then((res) => {
                                        if (res?.result && res?.result?.file) {
                                            downloadPDF(res?.result?.file, res?.result?.name);
                                        } else {
                                            dispatchGlobalData({
                                                type: setSnackbar,
                                                payload: {
                                                    snackbar: {
                                                        message: res.message !== "" ? t("printout-wizard.snackbars.handleErrors." + res.message): t("printout-wizard.snackbars.handleErrors.errors.generic"),
                                                        type: snackbarTypes.error,
                                                        show: true,
                                                        autoClose: true
                                                    }
                                                }
                                            });
                                        }
                                    });
                                }}>
                                <DownloadRounded />
                            </IconButton>
                            {hasScope(SCOPE_FINANCE_INTERMEDIARIES_DELETE_PRINTOUTS) &&
                                <IconButton title={t('preview.buttons.delete')} sx={{ color: theme.palette.accent.main }}
                                            onClick={() => {
                                                setOpenDeleteModal(true);
                                                setReportToDelete({ id: params.data.id, name: params.data.intermediaryName });
                                            }}>
                                    <DeleteRounded/>
                                </IconButton>
                            }
                        </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: {
            title: t('tables.printout.noRows.title'),
            content: t('tables.printout.noRows.content')
        }
    };
    const isRowSelectable = useMemo<IsRowSelectable>(() => {
        return (rowNode: RowNode) => {
            return rowNode.data?.intermediaryEmail && rowNode.data?.intermediaryEmail !== "";
        };
    }, []);

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    useEffect(() => {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(filterDefs, gridRef.current.api));
    }, [filterDefs])

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                getPrintoutsOfReport(filterDefs, id)
                    .then(response => {
                        let result = response.result;
                        if(result.status == "OK") {
                            setTotalDataCount(result.total);
                            if (result.total === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }

                            params.success({
                                rowData: result.content,
                                rowCount: result.total
                            })
                        } else {
                            navigate("/finance/intermediaries/")
                        }
                    })
                    .catch(() => params.fail());
            }
        };
    }
    const toolbar = {
        renderActions: () => (
            <Stack>
                {hasScope(SCOPE_FINANCE_INTERMEDIARIES_PUBLISH_PRINTOUTS) &&
                    <Button fullWidth color={"accent"} variant="contained" size={"small"}
                            onClick={() => {
                                const selectedRows = gridRef.current!.api.getSelectedRows();
                                if (selectedRows.length > 0) {
                                    props.onPublishClick();
                                }
                            }}
                            disabled={!isVisibleMail}>
                        {t('printout-wizard.buttons.email')}
                    </Button>
                }
            </Stack>
        )
    }

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                noRowsOverlayComponent={noRowsOverlayComponent}
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                rowData={props.data} rowBuffer={0}
                rowSelection={'multiple'} isRowSelectable={isRowSelectable}
                suppressRowClickSelection
                onGridReady={() => {
                    gridRef.current!.api.sizeColumnsToFit();
                    gridRef.current!.api.hideOverlay();
                }}
                onFirstDataRendered={() => {
                }}
                onRowSelected={(event: any) => {
                    props.onRowSelected(event.node.data.id, event.node.isSelected(), event.node.data);
                }}
                onSelectionChanged={() => {
                    props.setSelectedDataLength(gridRef.current!.api.getSelectedRows().length);
                    if (gridRef.current!.api.getSelectedRows().length > 0) {
                        setEmailVisible(true);
                    } else {
                        setEmailVisible(false);
                    }
                }}

            />
            <FinanceDialog
                title={t('preview.buttons.deleteTitle')}
                content={t('preview.buttons.deleteContent', { reportName: reportToDelete.name })}
                cancelButton={t('preview.buttons.deleteCancel')}
                callback={() => {
                    deleteIntermediaryPrintoutItem(reportToDelete.id).then((res) => {
                        if (res.status === 'OK') {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t('printout-wizard.snackbars.deleteSuccessPrintout'),
                                        type: snackbarTypes.success,
                                        show: true,
                                        autoClose: true
                                    }
                                }
                            });
                            setUpdating(true);
                        } else {
                            dispatchGlobalData({
                                type: setSnackbar,
                                payload: {
                                    snackbar: {
                                        message: t('printout-wizard.snackbars.deleteErrorPrintout'),
                                        type: snackbarTypes.error,
                                        show: true,
                                        autoClose: true
                                    }
                                }
                            });
                        }
                    })
                }}
                confirmButton={t('preview.buttons.deleteConfirm')}
                open={openDeleteModal}
                onClose={() => {
                    setOpenDeleteModal(false)
                }}
                titleIcon={<InfoRounded sx={{ color: 'accent.main' }}/>}
            />
        </>

    );
}

export default ReportPreviewTable;